import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Users, ShoppingBag, TrendingUp, Wallet, Share2, Crown, Shield, Gift, HelpCircle, RotateCcw, Mic, Moon, Headphones, CreditCard, Music, Sparkles, Save, Plus, Trash2, ArrowLeft, Edit3, Video, BarChart3, BookOpen, X, Check, Settings, UserPlus, Mail, Upload, MessageCircle, Lock, Award } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { studentAPI, classroomManageAPI, wordBankAPI, classroomAPI, authAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import StudentsTab from '@/components/guardian/StudentsTab';
import MarketplaceTab from '@/components/guardian/MarketplaceTab';
import ProgressTab from '@/components/guardian/ProgressTab';
import IntegrityReportTab from '@/components/guardian/IntegrityReportTab';
import WalletTab from '@/components/guardian/WalletTab';
import ReferralTab from '@/components/guardian/ReferralTab';
import SubscriptionTab from '@/components/guardian/SubscriptionTab';
import OffersTab from '@/components/guardian/OffersTab';
import AffiliateTab from '@/components/guardian/AffiliateTab';
import AudioMemoryTab from '@/components/guardian/AudioMemoryTab';
import BedtimeStoriesTab from '@/components/guardian/BedtimeStoriesTab';
import ReadingBeeTab from '@/components/guardian/ReadingBeeTab';
import AudioBookCollection from '@/components/AudioBookCollection';
import UserIDCards from '@/components/guardian/UserIDCards';
import OnboardingWizard from '@/components/OnboardingWizard';
import { guardianOnboardingSteps, teacherOnboardingSteps } from '@/components/onboardingSteps';
import FAQSection from '@/components/FAQSection';
import ParentMediaTab from '@/components/guardian/ParentMediaTab';
import MusicCreditsTab from '@/components/guardian/MusicCreditsTab';
import { parentFAQ } from '@/components/faqContent';
import StudentFormDialog from '@/components/guardian/StudentFormDialog';

const GuardianPortal = () => {
  const { user, logout, logActivity } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('students');
  const [wizardKey, setWizardKey] = useState(0);

  useEffect(() => {
    logActivity('page_view', '/portal', { tab: activeTab });
  }, [activeTab, logActivity]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const resetOnboarding = () => {
    localStorage.removeItem(`sv_onboarding_guardian_${user?.id || user?.email}`);
    setWizardKey((k) => k + 1);
  };

  const tabs = [
    { id: 'students', label: t('guardian.students'), icon: Users },
    ...((user && (user.is_teacher === true || user.role === 'admin')) ? [{ id: 'classrooms', label: 'Classrooms', icon: Users }] : []),
    { id: 'marketplace', label: 'Word Bank', icon: ShoppingBag },
    { id: 'audio-memories', label: 'Audio Memories', icon: Mic },
    { id: 'bedtime-stories', label: 'Bedtime Stories', icon: Moon },
    { id: 'audio-books', label: 'Audio Books', icon: Headphones },
    { id: 'reading-bee', label: 'Reading Bee', icon: Award },
    { id: 'media', label: 'Music & Media', icon: Music },
    { id: 'music-credits', label: 'Music Credits', icon: Music },
    { id: 'progress', label: t('guardian.progress'), icon: TrendingUp },
    { id: 'integrity', label: 'Integrity', icon: Shield },
    { id: 'id-cards', label: 'ID Cards', icon: CreditCard },
    { id: 'referral', label: t('guardian.inviteEarn'), icon: Share2 },
    { id: 'subscription', label: 'Subscription', icon: Crown },
    { id: 'wallet', label: t('guardian.wallet'), icon: Wallet },
    { id: 'offers', label: 'Offers', icon: Gift },
    { id: 'affiliate', label: 'Affiliate', icon: Share2 },
    { id: 'life-lessons', label: 'Life Lessons', icon: Sparkles },
    { id: 'account', label: 'Account', icon: Settings },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
  ];

  const rightContent = (
    <div className="flex items-center gap-2 flex-wrap">
      {user?.is_teacher && (
        <span
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-bold"
          style={{ color: '#059669', border: '1px solid rgba(5,150,105,0.3)', background: 'rgba(5,150,105,0.1)' }}
          data-testid="teacher-badge">
          <Crown size={14} /> Teacher Account{user?.school_name ? ` — ${user.school_name}` : ''}
        </span>
      )}
      {(user?.role === 'admin' || user?.is_delegated_admin) && (
        <button onClick={() => navigate('/admin')}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all hover:scale-105"
          style={{ color: '#D4A853', border: '1px solid rgba(212,168,83,0.3)', background: 'rgba(212,168,83,0.08)' }}
          data-testid="admin-link">
          <Shield size={14} /> Admin
        </button>
      )}
      <button onClick={resetOnboarding}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
        style={{ color: '#94A3B8', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}
        data-testid="reset-onboarding-btn">
        <RotateCcw size={14} /> Tutorial
      </button>
    </div>
  );

  return (
    <AppShell
      title={user && user.is_teacher === true ? `Educator Portal — ${user.school_name || 'My School'}` : 'Guardian Portal'}
      subtitle={user && user.is_teacher === true ? `Welcome, Teacher ${user.full_name || ''}!` : `Welcome, ${user?.full_name || ''}!`}
      onLogout={handleLogout}
      rightContent={rightContent}
    >
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Horizontally scrollable tabs */}
        {/* Impersonation Banner */}
        {(() => {
          const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (savedUser?._impersonated) {
            return (
              <div className="mb-4 p-3 border-4 border-amber-500 bg-amber-50 flex items-center justify-between" data-testid="impersonation-banner">
                <p className="font-bold text-sm text-amber-800">
                  Viewing as: <span className="text-amber-900">{savedUser.full_name || savedUser.email}</span> (Admin Preview)
                </p>
                <button onClick={() => {
                  const origToken = localStorage.getItem('admin_original_token');
                  const origUser = localStorage.getItem('admin_original_user');
                  if (origToken && origUser) {
                    localStorage.setItem('token', origToken);
                    localStorage.setItem('user', origUser);
                    localStorage.removeItem('admin_original_token');
                    localStorage.removeItem('admin_original_user');
                    window.location.href = '/admin';
                  }
                }}
                  className="px-4 py-1.5 bg-amber-600 text-white font-bold text-sm border-2 border-black hover:bg-amber-700 transition-all"
                  data-testid="exit-impersonation-btn">
                  Exit to Admin
                </button>
              </div>
            );
          }
          return null;
        })()}

        <div className="mb-4 sm:mb-6 -mx-3 sm:mx-0 px-3 sm:px-0 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1.5 sm:gap-2 pb-2 sm:flex-wrap min-w-max sm:min-w-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`sv-tab flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 sm:flex-shrink ${isActive ? 'sv-tab-active' : ''}`}
                  data-testid={`tab-${tab.id}`}>
                  <Icon size={16} /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          {activeTab === 'students' && <StudentsTab />}
          {activeTab === 'classrooms' && <ClassroomsTab />}
          {activeTab === 'subscription' && <SubscriptionTab />}
          {activeTab === 'marketplace' && <MarketplaceTab />}
          {activeTab === 'wallet' && <WalletTab />}
          {activeTab === 'referral' && <ReferralTab />}
          {activeTab === 'offers' && <OffersTab />}
          {activeTab === 'affiliate' && <AffiliateTab />}
          {activeTab === 'audio-memories' && <AudioMemoryTab />}
          {activeTab === 'bedtime-stories' && <BedtimeStoriesTab />}
          {activeTab === 'audio-books' && <AudioBookCollection embedded />}
          {activeTab === 'reading-bee' && <ReadingBeeTab />}
          {activeTab === 'media' && <ParentMediaTab />}
          {activeTab === 'music-credits' && <MusicCreditsTab />}
          {activeTab === 'id-cards' && <UserIDCards />}
          {activeTab === 'progress' && <ProgressTab />}
          {activeTab === 'integrity' && <IntegrityReportTab />}
          {activeTab === 'life-lessons' && <LifeLessonsTab />}
          {activeTab === 'account' && <AccountTab />}
          {activeTab === 'faq' && <FAQSection items={parentFAQ} title={user?.is_teacher ? "Educator FAQ" : "Parent & Guardian FAQ"} />}
        </div>
      </div>

      <OnboardingWizard
        key={wizardKey}
        steps={user?.is_teacher ? teacherOnboardingSteps : guardianOnboardingSteps}
        portalType={user?.is_teacher ? 'teacher' : 'guardian'}
        userId={user?.id || user?.email}
      />
    </AppShell>
  );
};

/* ============================== Life Lessons Tab ============================== */

const RELATIONSHIP_OPTIONS = [
  { value: 'brother_sister', label: 'Brother/Sister' },
  { value: 'best_friend', label: 'Best Friend' },
  { value: 'classmate', label: 'Classmate' },
  { value: 'bully', label: 'Bully' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'coach', label: 'Coach' },
  { value: 'neighbor', label: 'Neighbor' },
  { value: 'cousin', label: 'Cousin' },
  { value: 'online_friend', label: 'Online Friend' },
  { value: 'other', label: 'Other' },
];

const DELIVERY_METHODS = [
  { value: 'mentor_character', label: 'Through a wise mentor character' },
  { value: 'friend_advice', label: "Through a friend's advice" },
  { value: 'story_moral', label: "Through the story's events" },
  { value: 'inner_voice', label: 'As an inner realization' },
];

const LifeLessonsTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [characters, setCharacters] = React.useState([]);
  const [lessons, setLessons] = React.useState([]);
  const [showCharForm, setShowCharForm] = React.useState(false);
  const [showLessonForm, setShowLessonForm] = React.useState(false);
  const [editingCharIdx, setEditingCharIdx] = React.useState(null);
  const [editingLessonIdx, setEditingLessonIdx] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // New character form state
  const emptyChar = { id: '', name: '', relationship: 'classmate', relationship_type: 'neutral', description: '', influence_level: 'medium', applies_to: [] };
  const emptyLesson = { id: '', topic: '', character_name: '', problem: '', parent_solution: '', delivery_method: 'story_moral', active: true, applies_to: [] };
  const [charForm, setCharForm] = React.useState({ ...emptyChar });
  const [lessonForm, setLessonForm] = React.useState({ ...emptyLesson });

  // Fetch students
  const { data: students = [], isLoading } = useQuery({
    queryKey: ['students', user?.id],
    queryFn: async () => {
      const response = await studentAPI.getAll(user?.id);
      return response.data;
    },
    enabled: !!user?.id,
  });

  // Load characters & lessons from all students on mount
  React.useEffect(() => {
    if (students.length === 0) return;
    const charMap = {};
    const lessonMap = {};
    students.forEach((s) => {
      (s.life_characters || []).forEach((c) => {
        const key = c.id || c.name;
        if (!charMap[key]) {
          charMap[key] = { ...c, applies_to: [s._id || s.id] };
        } else {
          charMap[key].applies_to = [...new Set([...charMap[key].applies_to, s._id || s.id])];
        }
      });
      (s.life_lessons || []).forEach((l) => {
        const key = l.id || l.topic;
        if (!lessonMap[key]) {
          lessonMap[key] = { ...l, applies_to: [s._id || s.id] };
        } else {
          lessonMap[key].applies_to = [...new Set([...lessonMap[key].applies_to, s._id || s.id])];
        }
      });
    });
    setCharacters(Object.values(charMap));
    setLessons(Object.values(lessonMap));
  }, [students]);

  const studentIds = students.map((s) => s._id || s.id);

  const toggleStudentForChar = (charIdx, studentId) => {
    const updated = [...characters];
    const arr = updated[charIdx].applies_to || [];
    updated[charIdx].applies_to = arr.includes(studentId) ? arr.filter((id) => id !== studentId) : [...arr, studentId];
    setCharacters(updated);
  };

  const toggleStudentForLesson = (lessonIdx, studentId) => {
    const updated = [...lessons];
    const arr = updated[lessonIdx].applies_to || [];
    updated[lessonIdx].applies_to = arr.includes(studentId) ? arr.filter((id) => id !== studentId) : [...arr, studentId];
    setLessons(updated);
  };

  const selectAllStudentsForChar = (charIdx) => {
    const updated = [...characters];
    const allSelected = studentIds.every((id) => (updated[charIdx].applies_to || []).includes(id));
    updated[charIdx].applies_to = allSelected ? [] : [...studentIds];
    setCharacters(updated);
  };

  const selectAllStudentsForLesson = (lessonIdx) => {
    const updated = [...lessons];
    const allSelected = studentIds.every((id) => (updated[lessonIdx].applies_to || []).includes(id));
    updated[lessonIdx].applies_to = allSelected ? [] : [...studentIds];
    setLessons(updated);
  };

  const addCharacter = () => {
    if (!charForm.name.trim()) { toast.error('Character name is required'); return; }
    const newChar = { ...charForm, id: Date.now().toString() };
    setCharacters([...characters, newChar]);
    setCharForm({ ...emptyChar });
    setShowCharForm(false);
  };

  const updateCharacter = () => {
    if (editingCharIdx === null) return;
    const updated = [...characters];
    updated[editingCharIdx] = { ...charForm, applies_to: updated[editingCharIdx].applies_to };
    setCharacters(updated);
    setEditingCharIdx(null);
    setCharForm({ ...emptyChar });
    setShowCharForm(false);
  };

  const deleteCharacter = (idx) => {
    setCharacters(characters.filter((_, i) => i !== idx));
  };

  const addLesson = () => {
    if (!lessonForm.topic.trim()) { toast.error('Lesson topic is required'); return; }
    const newLesson = { ...lessonForm, id: Date.now().toString() };
    setLessons([...lessons, newLesson]);
    setLessonForm({ ...emptyLesson });
    setShowLessonForm(false);
  };

  const updateLesson = () => {
    if (editingLessonIdx === null) return;
    const updated = [...lessons];
    updated[editingLessonIdx] = { ...lessonForm, applies_to: updated[editingLessonIdx].applies_to };
    setLessons(updated);
    setEditingLessonIdx(null);
    setLessonForm({ ...emptyLesson });
    setShowLessonForm(false);
  };

  const deleteLesson = (idx) => {
    setLessons(lessons.filter((_, i) => i !== idx));
  };

  const toggleLessonActive = (idx) => {
    const updated = [...lessons];
    updated[idx].active = !updated[idx].active;
    setLessons(updated);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      let saved = 0;
      for (const s of students) {
        const sid = s._id || s.id;
        const studentChars = characters.filter((c) => (c.applies_to || []).includes(sid)).map(({ applies_to, ...rest }) => rest);
        const studentLessons = lessons.filter((l) => (l.applies_to || []).includes(sid)).map(({ applies_to, ...rest }) => rest);
        // Always send update — include a dummy field so it's never empty
        await api.patch(`/students/${sid}`, {
          life_characters: studentChars,
          life_lessons: studentLessons,
          full_name: s.full_name,
        });
        saved++;
      }
      queryClient.invalidateQueries({ queryKey: ['students'] });
      toast.success(`Life characters & lessons saved for ${saved} students`);
    } catch (err) {
      toast.error('Failed to save: ' + (err?.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const relLabel = (val) => RELATIONSHIP_OPTIONS.find((r) => r.value === val)?.label || val;
  const typeBadge = (t) => {
    if (t === 'positive') return { bg: '#d1fae5', color: '#065f46', label: 'Positive' };
    if (t === 'negative') return { bg: '#fee2e2', color: '#991b1b', label: 'Challenge' };
    return { bg: '#e5e7eb', color: '#374151', label: 'Neutral' };
  };

  if (isLoading) return <div className="text-center py-8 text-gray-400">Loading students...</div>;
  if (students.length === 0) return <div className="text-center py-8 text-gray-400">No students found. Add students first.</div>;

  return (
    <div className="space-y-6">
      {/* ---- Characters Section ---- */}
      <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Sparkles size={20} style={{ color: '#6366f1' }} /> Life Characters
          </h3>
          <button
            onClick={() => { setCharForm({ ...emptyChar }); setEditingCharIdx(null); setShowCharForm(!showCharForm); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-sm">
            <Plus size={14} /> Add Character
          </button>
        </div>
        <p className="text-xs mb-4" style={{ color: '#8c8780' }}>
          Add real people in your children's lives. Check which students each character applies to.
        </p>

        {/* Character Form */}
        {showCharForm && (
          <div className="p-4 rounded-lg mb-4" style={{ background: '#f5f3ee', border: '1px solid #e8e5de' }}>
            <p className="text-xs font-bold uppercase mb-3" style={{ color: '#6366f1' }}>
              {editingCharIdx !== null ? 'Edit Character' : 'New Character'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input value={charForm.name} onChange={(e) => setCharForm({ ...charForm, name: e.target.value })}
                placeholder="Name" className="px-3 py-2 rounded-lg text-sm border border-gray-300 font-bold bg-white" style={{ color: '#111827' }} />
              <select value={charForm.relationship} onChange={(e) => setCharForm({ ...charForm, relationship: e.target.value })}
                className="px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white" style={{ color: '#111827' }}>
                {RELATIONSHIP_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mb-3">
              {['positive', 'neutral', 'negative'].map((t) => (
                <button key={t} onClick={() => setCharForm({ ...charForm, relationship_type: t })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${charForm.relationship_type === t
                    ? (t === 'positive' ? 'bg-emerald-500 text-white' : t === 'negative' ? 'bg-red-500 text-white' : 'bg-gray-500 text-white')
                    : 'bg-gray-100 text-gray-500'}`}>
                  {t === 'positive' ? 'Positive' : t === 'negative' ? 'Challenge' : 'Neutral'}
                </button>
              ))}
              <select value={charForm.influence_level} onChange={(e) => setCharForm({ ...charForm, influence_level: e.target.value })}
                className="px-3 py-1.5 rounded-lg text-xs border border-gray-300 ml-auto bg-white" style={{ color: '#111827' }}>
                <option value="low">Low influence</option>
                <option value="medium">Medium influence</option>
                <option value="high">High influence</option>
              </select>
            </div>
            <textarea value={charForm.description} onChange={(e) => setCharForm({ ...charForm, description: e.target.value })}
              placeholder="Brief description (e.g., 'A boy in class who teases him about reading')"
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-300 resize-none bg-white mb-3" style={{ color: '#111827' }} rows={2} />
            <div className="flex gap-2">
              <button onClick={editingCharIdx !== null ? updateCharacter : addCharacter}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all flex items-center gap-1.5">
                <Save size={14} /> {editingCharIdx !== null ? 'Update' : 'Add'}
              </button>
              <button onClick={() => { setShowCharForm(false); setEditingCharIdx(null); }}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Characters Table */}
        {characters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Name</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Relationship</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Type</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Influence</th>
                  {students.map((s) => (
                    <th key={s._id || s.id} className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">{s.full_name || s.name}</th>
                  ))}
                  <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">All</th>
                  <th className="text-right py-2 px-2 text-xs font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {characters.map((char, idx) => {
                  const badge = typeBadge(char.relationship_type);
                  return (
                    <tr key={char.id || idx} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <td className="py-2.5 px-2 font-bold text-gray-900">{char.name}</td>
                      <td className="py-2.5 px-2 text-gray-600">{relLabel(char.relationship)}</td>
                      <td className="py-2.5 px-2">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                      </td>
                      <td className="py-2.5 px-2 text-gray-600 capitalize">{char.influence_level}</td>
                      {students.map((s) => {
                        const sid = s._id || s.id;
                        return (
                          <td key={sid} className="py-2.5 px-2 text-center">
                            <input type="checkbox" checked={(char.applies_to || []).includes(sid)}
                              onChange={() => toggleStudentForChar(idx, sid)}
                              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                          </td>
                        );
                      })}
                      <td className="py-2.5 px-2 text-center">
                        <input type="checkbox"
                          checked={studentIds.every((id) => (char.applies_to || []).includes(id))}
                          onChange={() => selectAllStudentsForChar(idx)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { setCharForm({ ...char }); setEditingCharIdx(idx); setShowCharForm(true); }}
                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 px-2 py-1">Edit</button>
                          <button onClick={() => deleteCharacter(idx)}
                            className="text-xs font-bold text-red-500 hover:text-red-700 px-1 py-1"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs italic text-center py-4" style={{ color: '#b0aba4' }}>No characters added yet. Click "Add Character" to get started.</p>
        )}
      </div>

      {/* ---- Lessons Section ---- */}
      <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(212,168,83,0.25)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Sparkles size={20} style={{ color: '#D4A853' }} /> Life Lessons
          </h3>
          <button
            onClick={() => { setLessonForm({ ...emptyLesson }); setEditingLessonIdx(null); setShowLessonForm(!showLessonForm); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:opacity-90 transition-all shadow-sm"
            style={{ background: '#D4A853' }}>
            <Plus size={14} /> Add Lesson
          </button>
        </div>
        <p className="text-xs mb-4" style={{ color: '#8c8780' }}>
          Add life lessons you want woven into stories. The AI will deliver your wisdom through trusted story characters so your child absorbs the lesson naturally.
        </p>

        {/* Lesson Form */}
        {showLessonForm && (
          <div className="p-4 rounded-lg mb-4" style={{ background: 'rgba(212,168,83,0.06)', border: '1px solid rgba(212,168,83,0.2)' }}>
            <p className="text-xs font-bold uppercase mb-3" style={{ color: '#D4A853' }}>
              {editingLessonIdx !== null ? 'Edit Lesson' : 'New Lesson'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input value={lessonForm.topic} onChange={(e) => setLessonForm({ ...lessonForm, topic: e.target.value })}
                placeholder="Topic (e.g., Dealing with bullies)" className="px-3 py-2 rounded-lg text-sm border border-gray-300 font-bold bg-white" style={{ color: '#111827' }} />
              <select value={lessonForm.character_name} onChange={(e) => setLessonForm({ ...lessonForm, character_name: e.target.value })}
                className="px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white" style={{ color: '#111827' }}>
                <option value="">Related to...</option>
                {characters.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                <option value="general">General life lesson</option>
              </select>
            </div>
            <textarea value={lessonForm.problem} onChange={(e) => setLessonForm({ ...lessonForm, problem: e.target.value })}
              placeholder="The problem (e.g., Marcus teases SJ about reading and calls him a nerd)"
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-300 resize-none bg-white mb-3" style={{ color: '#111827' }} rows={2} />
            <textarea value={lessonForm.parent_solution} onChange={(e) => setLessonForm({ ...lessonForm, parent_solution: e.target.value })}
              placeholder="Your solution -- what you want your child to learn (e.g., Stand tall, look them in the eye...)"
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-300 resize-none bg-white mb-3" style={{ color: '#111827' }} rows={3}
              style={{ background: 'rgba(212,168,83,0.08)' }} />
            <div className="flex gap-3 mb-3">
              <select value={lessonForm.delivery_method} onChange={(e) => setLessonForm({ ...lessonForm, delivery_method: e.target.value })}
                className="flex-1 px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white" style={{ color: '#111827' }}>
                {DELIVERY_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={editingLessonIdx !== null ? updateLesson : addLesson}
                className="px-4 py-2 rounded-lg text-xs font-bold text-white hover:opacity-90 transition-all flex items-center gap-1.5"
                style={{ background: '#D4A853' }}>
                <Save size={14} /> {editingLessonIdx !== null ? 'Update' : 'Add'}
              </button>
              <button onClick={() => { setShowLessonForm(false); setEditingLessonIdx(null); }}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Lessons Table */}
        {lessons.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(212,168,83,0.3)' }}>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Topic</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Character</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Problem</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Method</th>
                  {students.map((s) => (
                    <th key={s._id || s.id} className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">{s.full_name || s.name}</th>
                  ))}
                  <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">All</th>
                  <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">Active</th>
                  <th className="text-right py-2 px-2 text-xs font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((lesson, idx) => (
                  <tr key={lesson.id || idx} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td className="py-2.5 px-2 font-bold text-gray-900">{lesson.topic}</td>
                    <td className="py-2.5 px-2 text-gray-600">{lesson.character_name || '--'}</td>
                    <td className="py-2.5 px-2 text-gray-600 max-w-[150px] truncate">{lesson.problem || '--'}</td>
                    <td className="py-2.5 px-2 text-gray-600 text-xs">
                      {DELIVERY_METHODS.find((m) => m.value === lesson.delivery_method)?.label || lesson.delivery_method}
                    </td>
                    {students.map((s) => {
                      const sid = s._id || s.id;
                      return (
                        <td key={sid} className="py-2.5 px-2 text-center">
                          <input type="checkbox" checked={(lesson.applies_to || []).includes(sid)}
                            onChange={() => toggleStudentForLesson(idx, sid)}
                            className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                        </td>
                      );
                    })}
                    <td className="py-2.5 px-2 text-center">
                      <input type="checkbox"
                        checked={studentIds.every((id) => (lesson.applies_to || []).includes(id))}
                        onChange={() => selectAllStudentsForLesson(idx)}
                        className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <button onClick={() => toggleLessonActive(idx)}
                        className={`px-2 py-1 rounded text-[10px] font-bold ${lesson.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                        {lesson.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setLessonForm({ ...lesson }); setEditingLessonIdx(idx); setShowLessonForm(true); }}
                          className="text-xs font-bold text-amber-600 hover:text-amber-800 px-2 py-1">Edit</button>
                        <button onClick={() => deleteLesson(idx)}
                          className="text-xs font-bold text-red-500 hover:text-red-700 px-1 py-1"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs italic text-center py-4" style={{ color: '#b0aba4' }}>No lessons added yet. Click "Add Lesson" to get started.</p>
        )}
      </div>

      {/* Save All Button */}
      <div className="flex justify-end">
        <button onClick={handleSaveAll} disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 shadow-md"
          style={{ background: '#6366f1' }}>
          <Save size={16} />
          {saving ? 'Saving...' : 'Save All Characters & Lessons'}
        </button>
      </div>
    </div>
  );
};

/* ============================== Classrooms Tab (Teacher Only) ============================== */

const GRADE_OPTIONS = ['Pre-K', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const ClassroomsTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Views: 'list' | 'detail' | 'create' | 'edit'
  const [view, setView] = React.useState('list');
  const [selectedClassroom, setSelectedClassroom] = React.useState(null);
  const [formData, setFormData] = React.useState({ name: '', description: '', grade_level: '3' });
  const [addingStudents, setAddingStudents] = React.useState(false);
  const [addingBanks, setAddingBanks] = React.useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = React.useState([]);
  const [selectedBankIds, setSelectedBankIds] = React.useState([]);
  const [showAnalytics, setShowAnalytics] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [newStudentFormOpen, setNewStudentFormOpen] = React.useState(false);

  // Fetch classrooms
  const { data: classrooms = [], isLoading: loadingClassrooms, refetch: refetchClassrooms } = useQuery({
    queryKey: ['classrooms'],
    queryFn: async () => {
      const res = await classroomManageAPI.getAll();
      return res.data;
    },
  });

  // Fetch all students accessible to this teacher (owned + linked via teacher_ids)
  const { data: allStudents = [] } = useQuery({
    queryKey: ['students', user?.id],
    queryFn: async () => {
      const res = await studentAPI.getAll();
      return res.data;
    },
    enabled: !!user?.id,
  });

  // Fetch word banks
  const { data: allBanks = [] } = useQuery({
    queryKey: ['wordBanks'],
    queryFn: async () => {
      const res = await wordBankAPI.getAll();
      return res.data?.banks || res.data || [];
    },
  });

  // Fetch classroom detail
  const { data: classroomDetail, isLoading: loadingDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['classroom', selectedClassroom?.id],
    queryFn: async () => {
      const res = await classroomManageAPI.getById(selectedClassroom.id);
      return res.data;
    },
    enabled: !!selectedClassroom?.id && view === 'detail',
  });

  // Fetch classroom analytics
  const { data: analyticsData, isLoading: loadingAnalytics } = useQuery({
    queryKey: ['classroomAnalytics', selectedClassroom?.id],
    queryFn: async () => {
      const res = await classroomManageAPI.getAnalytics(selectedClassroom.id);
      return res.data;
    },
    enabled: !!selectedClassroom?.id && showAnalytics,
  });

  // Create classroom
  const createMutation = useMutation({
    mutationFn: (data) => classroomManageAPI.create(data),
    onSuccess: () => {
      toast.success('Classroom created!');
      refetchClassrooms();
      setView('list');
      setFormData({ name: '', description: '', grade_level: '3' });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to create classroom'),
  });

  // Update classroom
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => classroomManageAPI.update(id, data),
    onSuccess: () => {
      toast.success('Classroom updated!');
      refetchClassrooms();
      refetchDetail();
      setView('detail');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to update classroom'),
  });

  // Delete classroom
  const deleteMutation = useMutation({
    mutationFn: ({ id, deleteStudents }) => classroomManageAPI.delete(id, deleteStudents),
    onSuccess: (response) => {
      const data = response.data;
      if (data.students_deleted > 0) {
        toast.success(`Classroom deleted with ${data.students_deleted} student(s)`);
        queryClient.invalidateQueries(['students']);
        queryClient.invalidateQueries(['subscription']);
      } else {
        toast.success('Classroom deleted');
      }
      refetchClassrooms();
      setView('list');
      setSelectedClassroom(null);
      setDeleteDialogOpen(false);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to delete classroom'),
  });

  // Add students
  const addStudentsMutation = useMutation({
    mutationFn: ({ id, studentIds }) => classroomManageAPI.addStudents(id, studentIds),
    onSuccess: () => {
      toast.success('Students added!');
      refetchDetail();
      setAddingStudents(false);
      setSelectedStudentIds([]);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to add students'),
  });

  // Remove student
  const removeStudentMutation = useMutation({
    mutationFn: ({ classroomId, studentId }) => classroomManageAPI.removeStudent(classroomId, studentId),
    onSuccess: () => {
      toast.success('Student removed');
      refetchDetail();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to remove student'),
  });

  // Assign banks
  const assignBanksMutation = useMutation({
    mutationFn: ({ id, bankIds }) => classroomManageAPI.assignBanks(id, bankIds),
    onSuccess: () => {
      toast.success('Word banks assigned!');
      refetchDetail();
      setAddingBanks(false);
      setSelectedBankIds([]);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to assign banks'),
  });

  // Go Live
  const goLiveMutation = useMutation({
    mutationFn: async () => {
      // Create a classroom session linked to this classroom
      const res = await classroomAPI.create({
        title: `${classroomDetail?.name || 'Classroom'} Live Session`,
        bank_ids: classroomDetail?.assigned_bank_ids || [],
        description: `Live session for ${classroomDetail?.name}`,
      });
      const session = res.data;
      const liveRes = await classroomAPI.goLive(session.id);
      return { session, liveRes: liveRes.data };
    },
    onSuccess: (data) => {
      toast.success('Live session started!');
      if (data.liveRes?.room_url) {
        window.open(data.liveRes.room_url, '_blank');
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Failed to start live session'),
  });

  const openDetail = (classroom) => {
    setSelectedClassroom(classroom);
    setView('detail');
    setShowAnalytics(false);
  };

  const openEdit = () => {
    setFormData({
      name: classroomDetail?.name || '',
      description: classroomDetail?.description || '',
      grade_level: classroomDetail?.grade_level || '3',
    });
    setView('edit');
  };

  const openCreate = () => {
    setFormData({ name: '', description: '', grade_level: '3' });
    setView('create');
  };

  const handleSaveForm = () => {
    if (!formData.name.trim()) { toast.error('Classroom name is required'); return; }
    if (view === 'create') {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate({ id: selectedClassroom.id, data: formData });
    }
  };

  // Determine students not in the classroom
  const detail = classroomDetail || selectedClassroom;
  const detailStudentIds = (detail?.student_ids || []);
  const detailStudents = detail?.students || [];
  const availableStudents = allStudents.filter((s) => !detailStudentIds.includes(s._id || s.id));

  // Available banks not yet assigned
  const assignedBankIds = detail?.assigned_bank_ids || [];

  // ===================== LIST VIEW =====================
  if (view === 'list') {
    if (loadingClassrooms) return <div className="text-center py-8 text-gray-400">Loading classrooms...</div>;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Users size={20} style={{ color: '#6366f1' }} /> My Classrooms
          </h2>
          <button onClick={openCreate}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all">
            <Plus size={14} /> Create Classroom
          </button>
        </div>

        {classrooms.length === 0 ? (
          <div className="text-center py-12 rounded-xl" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
            <Users size={48} className="mx-auto mb-4" style={{ color: '#d1d5db' }} />
            <p className="text-gray-500 font-semibold mb-2">No classrooms yet</p>
            <p className="text-xs text-gray-400 mb-4">Create your first classroom to organize students into groups.</p>
            <button onClick={openCreate}
              className="px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all">
              <Plus size={14} className="inline mr-1" /> Create Classroom
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {classrooms.map((c) => (
              <button key={c.id} onClick={() => openDetail(c)}
                className="text-left rounded-xl p-5 transition-all hover:shadow-md hover:scale-[1.01]"
                style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-gray-900 text-sm">{c.name}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                    {c.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {c.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{c.description}</p>}
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  {c.grade_level && <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold">Grade {c.grade_level}</span>}
                  <span className="flex items-center gap-1"><Users size={12} /> {(c.student_ids || []).length} students</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===================== CREATE / EDIT VIEW =====================
  if (view === 'create' || view === 'edit') {
    return (
      <div className="space-y-4 max-w-lg">
        <button onClick={() => { setView(view === 'edit' ? 'detail' : 'list'); }}
          className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 transition-all mb-2">
          <ArrowLeft size={14} /> Back
        </button>
        <h2 className="text-lg font-bold text-gray-900">
          {view === 'create' ? 'Create Classroom' : 'Edit Classroom'}
        </h2>
        <div className="rounded-xl p-5 space-y-4" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Classroom Name</label>
            <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., 3rd Grade Reading"
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-300 font-bold bg-white" style={{ color: '#111827' }} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Morning reading group"
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-300 resize-none bg-white" style={{ color: '#111827' }} rows={3} />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Grade Level</label>
            <select value={formData.grade_level} onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
              className="w-full px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white" style={{ color: '#111827' }}>
              {GRADE_OPTIONS.map((g) => <option key={g} value={g}>{g === 'Pre-K' || g === 'K' ? g : `Grade ${g}`}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={handleSaveForm}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50">
              <Save size={14} /> {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setView(view === 'edit' ? 'detail' : 'list'); }}
              className="px-5 py-2.5 rounded-lg text-sm font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all">
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================== DETAIL VIEW =====================
  if (view === 'detail') {
    if (loadingDetail) return <div className="text-center py-8 text-gray-400">Loading classroom...</div>;
    const d = classroomDetail || selectedClassroom;
    const students = d?.students || [];

    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => { setView('list'); setSelectedClassroom(null); setShowAnalytics(false); }}
            className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 transition-all">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">{d?.name}</h2>
            <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
              {d?.grade_level && <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold">Grade {d.grade_level}</span>}
              <span>{(d?.student_ids || []).length} students</span>
              {d?.description && <span>- {d.description}</span>}
            </div>
          </div>
          <button onClick={openEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all">
            <Edit3 size={14} /> Edit
          </button>
          <button onClick={() => setDeleteDialogOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-all">
            <Trash2 size={14} /> Delete
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => goLiveMutation.mutate()}
            disabled={goLiveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 transition-all disabled:opacity-50">
            <Video size={14} /> {goLiveMutation.isPending ? 'Starting...' : 'Go Live'}
          </button>
          <button onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-all">
            <BarChart3 size={14} /> {showAnalytics ? 'Hide Analytics' : 'View Analytics'}
          </button>
          <button onClick={async () => {
            const { chatAPI } = await import('@/lib/api');
            const newState = !d?.chat_enabled;
            try {
              await chatAPI.toggleClassroomChat(d.id, newState);
              refetchDetail();
              toast.success(`Classroom chat ${newState ? 'enabled' : 'disabled'}`);
            } catch (e) { toast.error('Failed to toggle chat'); }
          }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all ${d?.chat_enabled ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <MessageCircle size={14} /> {d?.chat_enabled ? 'Chat ON' : 'Chat OFF'}
          </button>
        </div>

        {/* Bulk lock/unlock students */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={async () => {
            if (!window.confirm('Lock ALL students in this classroom to music-only mode?')) return;
            const ids = d?.student_ids || [];
            for (const sid of ids) {
              await api.put(`/students/${sid}/classroom-lock`, { classroom_locked: true }).catch(() => {});
            }
            toast.success(`Locked ${ids.length} students to music-only mode`);
            refetchDetail();
          }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all">
            <Lock size={14} /> Pause Classroom (Music Only)
          </button>
          <button onClick={async () => {
            if (!window.confirm('Unlock ALL students in this classroom for full access?')) return;
            const ids = d?.student_ids || [];
            for (const sid of ids) {
              await api.put(`/students/${sid}/classroom-lock`, { classroom_locked: false }).catch(() => {});
            }
            toast.success(`Unlocked ${ids.length} students — full access restored`);
            refetchDetail();
          }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-all">
            <Check size={14} /> Resume Classroom (Full Access)
          </button>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(5,150,105,0.2)' }}>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
              <BarChart3 size={16} style={{ color: '#059669' }} /> Class Analytics
            </h3>
            {loadingAnalytics ? (
              <p className="text-xs text-gray-400">Loading analytics...</p>
            ) : analyticsData ? (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="rounded-lg p-3 text-center" style={{ background: '#f0fdf4' }}>
                    <p className="text-xl font-bold text-emerald-700">{analyticsData.class_summary?.avg_accuracy || 0}%</p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Avg Accuracy</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: '#eff6ff' }}>
                    <p className="text-xl font-bold text-blue-700">{analyticsData.class_summary?.total_assessments || 0}</p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Assessments</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: '#fefce8' }}>
                    <p className="text-xl font-bold text-amber-700">{analyticsData.class_summary?.total_words_mastered || 0}</p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Words Mastered</p>
                  </div>
                  <div className="rounded-lg p-3 text-center" style={{ background: '#fdf4ff' }}>
                    <p className="text-xl font-bold text-purple-700">{Math.round((analyticsData.class_summary?.avg_reading_seconds || 0) / 60)}m</p>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Avg Reading</p>
                  </div>
                </div>
                {analyticsData.students?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                          <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Student</th>
                          <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">Words</th>
                          <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">Accuracy</th>
                          <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">Assessments</th>
                          <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.students.map((s) => (
                          <tr key={s.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            <td className="py-2 px-2 font-bold text-gray-900">{s.name}</td>
                            <td className="py-2 px-2 text-center text-gray-600">{s.words_mastered}</td>
                            <td className="py-2 px-2 text-center text-gray-600">{s.avg_accuracy}%</td>
                            <td className="py-2 px-2 text-center text-gray-600">{s.assessments_completed}</td>
                            <td className="py-2 px-2 text-center text-gray-600">{s.score}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No analytics available.</p>
            )}
          </div>
        )}

        {/* Roster Section */}
        <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Users size={16} style={{ color: '#6366f1' }} /> Student Roster ({students.length})
            </h3>
            <div className="flex gap-2">
              <label className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition-all cursor-pointer shadow-sm">
                <Upload size={14} /> Import CSV
                <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const res = await studentAPI.bulkImport(file, selectedClassroom.id);
                    toast.success(`Imported ${res.data.created} students into ${selectedClassroom.name}!`);
                    queryClient.invalidateQueries(['classroom']);
                    queryClient.invalidateQueries(['classrooms']);
                    queryClient.invalidateQueries(['students']);
                    refetchDetail();
                  } catch (err) {
                    toast.error('Import failed: ' + (err?.response?.data?.detail || err.message));
                  }
                  e.target.value = '';
                }} />
              </label>
              <button onClick={() => { setAddingStudents(true); setSelectedStudentIds([]); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-sm">
                <UserPlus size={14} /> Add Existing
              </button>
              <button onClick={() => setNewStudentFormOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-all shadow-sm">
                <Plus size={14} /> Create New Student
              </button>
            </div>
          </div>

          {/* Add Students Dialog */}
          {addingStudents && (
            <div className="p-4 rounded-lg mb-4" style={{ background: '#f5f3ee', border: '1px solid #e8e5de' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase" style={{ color: '#6366f1' }}>Select Students to Add</p>
                <button onClick={() => setAddingStudents(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
              </div>
              {availableStudents.length === 0 ? (
                <div>
                  <p className="text-xs text-gray-400 italic mb-2">
                    {allStudents.length === 0
                      ? "You don't have any students yet. Create one first!"
                      : "All your students are already in this classroom."}
                  </p>
                  {allStudents.length === 0 && (
                    <button onClick={() => { setAddingStudents(false); setNewStudentFormOpen(true); }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-all">
                      <Plus size={14} /> Create Your First Student
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                  {availableStudents.map((s) => {
                    const sid = s._id || s.id;
                    const checked = selectedStudentIds.includes(sid);
                    return (
                      <label key={sid} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white cursor-pointer transition-all">
                        <input type="checkbox" checked={checked}
                          onChange={() => setSelectedStudentIds(checked ? selectedStudentIds.filter((id) => id !== sid) : [...selectedStudentIds, sid])}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-sm font-bold text-gray-900">{s.full_name}</span>
                        {s.grade && <span className="text-xs text-gray-400">Grade {s.grade}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {availableStudents.length > 0 && (
                <button onClick={() => addStudentsMutation.mutate({ id: d.id, studentIds: selectedStudentIds })}
                  disabled={selectedStudentIds.length === 0 || addStudentsMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50">
                  <Check size={14} /> {addStudentsMutation.isPending ? 'Adding...' : `Add ${selectedStudentIds.length} Student${selectedStudentIds.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          )}

          {/* Student Table */}
          {students.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                    <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Photo</th>
                    <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Name</th>
                    <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Age</th>
                    <th className="text-left py-2 px-2 text-xs font-bold text-gray-500 uppercase">Grade</th>
                    <th className="text-center py-2 px-2 text-xs font-bold text-gray-500 uppercase">Status</th>
                    <th className="text-right py-2 px-2 text-xs font-bold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id || s._id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <td className="py-2 px-2">
                        {s.photo_url ? (
                          <img src={s.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">
                            {(s.full_name || '?')[0]}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 font-bold text-gray-900">{s.full_name}</td>
                      <td className="py-2 px-2 text-gray-600">{s.age || '-'}</td>
                      <td className="py-2 px-2 text-gray-600">{s.grade || '-'}</td>
                      <td className="py-2 px-2 text-center">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">Enrolled</span>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <button onClick={() => removeStudentMutation.mutate({ classroomId: d.id, studentId: s.id || s._id })}
                          disabled={removeStudentMutation.isPending}
                          className="text-xs font-bold text-red-500 hover:text-red-700 px-2 py-1">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs italic text-center py-4" style={{ color: '#b0aba4' }}>No students in this classroom yet. Click "Add Students" to get started.</p>
          )}
        </div>

        {/* Word Banks Section */}
        <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(212,168,83,0.25)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <BookOpen size={16} style={{ color: '#D4A853' }} /> Assigned Word Banks ({assignedBankIds.length})
            </h3>
            <button onClick={() => { setAddingBanks(true); setSelectedBankIds([]); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all">
              <Plus size={14} /> Add Banks
            </button>
          </div>

          {/* Add Banks Dialog */}
          {addingBanks && (
            <div className="p-4 rounded-lg mb-4" style={{ background: 'rgba(212,168,83,0.06)', border: '1px solid rgba(212,168,83,0.2)' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase" style={{ color: '#D4A853' }}>Select Word Banks to Assign</p>
                <button onClick={() => setAddingBanks(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
              </div>
              {allBanks.filter((b) => !assignedBankIds.includes(b.id || b._id)).length === 0 ? (
                <p className="text-xs text-gray-400 italic">All available word banks are already assigned.</p>
              ) : (
                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                  {allBanks.filter((b) => !assignedBankIds.includes(b.id || b._id)).map((b) => {
                    const bid = b.id || b._id;
                    const checked = selectedBankIds.includes(bid);
                    return (
                      <label key={bid} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white cursor-pointer transition-all">
                        <input type="checkbox" checked={checked}
                          onChange={() => setSelectedBankIds(checked ? selectedBankIds.filter((id) => id !== bid) : [...selectedBankIds, bid])}
                          className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" />
                        <span className="text-sm font-bold text-gray-900">{b.name}</span>
                        {b.word_count != null && <span className="text-xs text-gray-400">{b.word_count} words</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {allBanks.filter((b) => !assignedBankIds.includes(b.id || b._id)).length > 0 && (
                <button onClick={() => assignBanksMutation.mutate({ id: d.id, bankIds: selectedBankIds })}
                  disabled={selectedBankIds.length === 0 || assignBanksMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white hover:opacity-90 transition-all disabled:opacity-50"
                  style={{ background: '#D4A853' }}>
                  <Check size={14} /> {assignBanksMutation.isPending ? 'Assigning...' : `Assign ${selectedBankIds.length} Bank${selectedBankIds.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          )}

          {/* Assigned Banks List */}
          {assignedBankIds.length > 0 ? (
            <div className="space-y-2">
              {assignedBankIds.map((bankId) => {
                const bank = allBanks.find((b) => (b.id || b._id) === bankId);
                return (
                  <div key={bankId} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#fefce8' }}>
                    <span className="text-sm font-bold text-gray-900">{bank?.name || bankId}</span>
                    <button onClick={() => {
                      const newIds = assignedBankIds.filter((id) => id !== bankId);
                      assignBanksMutation.mutate({ id: d.id, bankIds: newIds });
                    }}
                      className="text-xs font-bold text-red-500 hover:text-red-700">Remove</button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs italic text-center py-4" style={{ color: '#b0aba4' }}>No word banks assigned. Click "Add Banks" to assign word banks to this classroom.</p>
          )}
        </div>

        {/* Create New Student Dialog */}
        <StudentFormDialog
          isOpen={newStudentFormOpen}
          onClose={() => {
            setNewStudentFormOpen(false);
            refetchDetail();
            refetchClassrooms();
          }}
          student={null}
          guardianId={user?.id}
          classroomId={selectedClassroom?.id}
          classrooms={classrooms}
        />

        {/* Delete Classroom Dialog */}
        {deleteDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Classroom: {d?.name}</h3>
              <p className="text-sm text-gray-600 mb-6">How would you like to delete this classroom?</p>
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => deleteMutation.mutate({ id: d.id, deleteStudents: false })}
                  disabled={deleteMutation.isPending}
                  className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all"
                >
                  <p className="font-bold text-sm text-gray-900">Delete classroom only</p>
                  <p className="text-xs text-gray-500 mt-1">Keep all {(d?.student_ids || []).length} student(s) in your account</p>
                </button>
                <button
                  onClick={() => deleteMutation.mutate({ id: d.id, deleteStudents: true })}
                  disabled={deleteMutation.isPending}
                  className="w-full text-left p-4 rounded-lg border border-red-200 hover:border-red-400 hover:bg-red-50 transition-all"
                >
                  <p className="font-bold text-sm text-red-700">Delete classroom AND all students</p>
                  <p className="text-xs text-red-500 mt-1">Permanently delete {(d?.student_ids || []).length} student(s) and all their data</p>
                </button>
              </div>
              <button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleteMutation.isPending}
                className="w-full px-4 py-2 rounded-lg text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

/* ============================== Account Tab ============================== */

const AccountTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [saving, setSaving] = React.useState(false);
  const [profileForm, setProfileForm] = React.useState({
    full_name: '', phone: '', address: '', city: '', state: '', zip_code: '', country: '', fax: '', school_name: '', website: '',
  });
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('viewer');
  const [inviting, setInviting] = React.useState(false);

  // Fetch profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const res = await authAPI.getProfile();
      return res.data;
    },
    enabled: !!user?.id,
  });

  // Fetch team
  const { data: teamData, isLoading: teamLoading, refetch: refetchTeam } = useQuery({
    queryKey: ['team', user?.id],
    queryFn: async () => {
      const res = await authAPI.getTeam();
      return res.data;
    },
    enabled: !!user?.id,
  });

  // Populate form when profile loads
  React.useEffect(() => {
    if (profile) {
      setProfileForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip_code: profile.zip_code || '',
        country: profile.country || '',
        fax: profile.fax || '',
        school_name: profile.school_name || '',
        website: profile.website || '',
      });
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await authAPI.updateProfile(profileForm);
      queryClient.invalidateQueries(['profile']);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update profile');
    }
    setSaving(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Enter an email address'); return; }
    setInviting(true);
    try {
      await authAPI.inviteTeamMember({ email: inviteEmail.trim(), role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('viewer');
      refetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invitation');
    }
    setInviting(false);
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await authAPI.removeTeamMember(memberId);
      toast.success('Member removed');
      refetchTeam();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove member');
    }
  };

  if (profileLoading) return <div className="text-center py-8 text-gray-400">Loading profile...</div>;

  const inputClass = "w-full px-3 py-2 rounded-lg text-sm border border-gray-300 font-medium bg-white";
  const labelClass = "block text-xs font-bold text-gray-500 uppercase mb-1";

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Profile Card */}
      <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Settings size={20} style={{ color: '#6366f1' }} /> Profile Settings
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Full Name</label>
            <input value={profileForm.full_name} onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>Email (read-only)</label>
            <input value={profile?.email || ''} disabled
              className={`${inputClass} bg-gray-100 text-gray-500 cursor-not-allowed`} />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              placeholder="(555) 123-4567" className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>Address</label>
            <input value={profileForm.address} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>City</label>
            <input value={profileForm.city} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <input value={profileForm.state} onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>ZIP Code</label>
            <input value={profileForm.zip_code} onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>Country</label>
            <input value={profileForm.country} onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>Fax</label>
            <input value={profileForm.fax} onChange={(e) => setProfileForm({ ...profileForm, fax: e.target.value })}
              className={inputClass} style={{ color: '#111827' }} />
          </div>
          <div>
            <label className={labelClass}>Website</label>
            <input value={profileForm.website} onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
              placeholder="https://" className={inputClass} style={{ color: '#111827' }} />
          </div>
          {profile?.is_teacher && (
            <div className="sm:col-span-2">
              <label className={labelClass}>School Name</label>
              <input value={profileForm.school_name} onChange={(e) => setProfileForm({ ...profileForm, school_name: e.target.value })}
                className={inputClass} style={{ color: '#111827' }} />
            </div>
          )}
        </div>

        <button onClick={handleSaveProfile} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50">
          <Save size={14} /> {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      {/* Account Info */}
      <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Account Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase">Role</p>
            <p className="font-bold text-gray-900 capitalize">{profile?.role || 'guardian'}{profile?.is_teacher ? ' (Teacher)' : ''}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase">Member Since</p>
            <p className="font-bold text-gray-900">{profile?.created_date ? new Date(profile.created_date).toLocaleDateString() : '--'}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase">Referral Code</p>
            <p className="font-bold text-indigo-700 font-mono">{profile?.referral_code || '--'}</p>
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="rounded-xl p-5" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}>
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Users size={20} style={{ color: '#6366f1' }} /> Team Members
        </h3>
        <p className="text-xs mb-4" style={{ color: '#8c8780' }}>
          Invite team members to view or manage your account. They will have access based on the role you assign.
        </p>

        {/* Invite Form */}
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Email address"
            className="flex-1 min-w-[200px] px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white"
            style={{ color: '#111827' }}
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm border border-gray-300 bg-white"
            style={{ color: '#111827' }}
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
          <button
            onClick={handleInvite}
            disabled={inviting}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all disabled:opacity-50"
          >
            <UserPlus size={14} /> {inviting ? 'Sending...' : 'Invite'}
          </button>
        </div>

        {/* Active Members */}
        {teamLoading ? (
          <p className="text-xs text-gray-400">Loading team...</p>
        ) : (
          <div className="space-y-2">
            {(teamData?.members || []).length === 0 && (teamData?.invitations || []).length === 0 ? (
              <p className="text-xs italic text-center py-4" style={{ color: '#b0aba4' }}>No team members yet. Invite someone to get started.</p>
            ) : (
              <>
                {(teamData?.members || []).map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#f0fdf4', border: '1px solid #d1fae5' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{m.full_name || m.email}</p>
                      <p className="text-xs text-gray-500">{m.email}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 mr-2 capitalize">{m.team_role}</span>
                    <button onClick={() => handleRemoveMember(m.id)}
                      className="text-xs font-bold text-red-500 hover:text-red-700 px-2 py-1">
                      Remove
                    </button>
                  </div>
                ))}

                {/* Pending Invitations */}
                {(teamData?.invitations || []).filter((inv) => inv.status === 'pending').map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#fefce8', border: '1px solid #fef08a' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{inv.email}</p>
                      <p className="text-xs text-gray-500">Pending invitation</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 capitalize">{inv.role}</span>
                      <Mail size={12} className="text-amber-500" />
                      <button onClick={() => handleRemoveMember(inv.id)}
                        className="text-xs font-bold text-red-500 hover:text-red-700 px-2 py-1">
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuardianPortal;
